/** Bond Draw (Game36) constants & odds helpers — mirror backend `game36/constants` and `utils/odds.js`. */

export const TOTAL_NUMBERS = 36;
export const DRAW_COUNT = 3;
export const MAX_SELECTION = 6;
export const PRICE_PER_NUMBER = 500;
export const PLATFORM_FEE_RATE = 0.05;

export const CALIBRATED_ODDS_BY_SELECTION = {
  1: 10.82,
  2: 9.83,
  3: 8.89,
  4: 8.02,
  5: 7.19,
  6: 6.42,
};

function combinations(n, k) {
  if (k < 0 || k > n) return 0;
  if (k === 0 || k === n) return 1;
  k = Math.min(k, n - k);
  let result = 1;
  for (let i = 1; i <= k; i += 1) {
    result = (result * (n - k + i)) / i;
  }
  return result;
}

export function computeOdds(k) {
  return CALIBRATED_ODDS_BY_SELECTION[k] || 0;
}

export function computeStake(k) {
  return Math.max(0, Math.floor(Number(k) || 0)) * PRICE_PER_NUMBER;
}

export function computeHitProbability(k) {
  const remaining = TOTAL_NUMBERS - k;
  const noHit = combinations(remaining, DRAW_COUNT) / combinations(TOTAL_NUMBERS, DRAW_COUNT);
  return 1 - noHit;
}

export function computePotentialMaxPayout(k) {
  if (k <= 0) return 0;
  const stake = computeStake(k);
  const odds = computeOdds(k);
  const maxMatches = Math.min(k, DRAW_COUNT);
  return Math.round(stake + PRICE_PER_NUMBER * maxMatches * odds);
}

export function computePayout(k, matches) {
  if (matches <= 0) return 0;
  const stake = computeStake(k);
  const odds = computeOdds(k);
  return Math.round(stake + PRICE_PER_NUMBER * matches * odds);
}

export function buildTicketQuote(selectedCountOrRound) {
  const k = Math.max(
    0,
    Math.floor(
      Number(
        typeof selectedCountOrRound === "object"
          ? selectedCountOrRound?.selectedCount ?? selectedCountOrRound?.k
          : selectedCountOrRound
      ) || 0
    )
  );

  if (k <= 0) {
    return { k: 0, stake: 0, odds: 0, maxWin: 0, reserve: 0, hitProbability: 0 };
  }

  const stake = computeStake(k);
  const odds = computeOdds(k);
  const maxWin = computePotentialMaxPayout(k);
  const reserve = Math.max(0, maxWin - stake);

  return {
    k,
    stake,
    odds,
    maxWin,
    reserve,
    hitProbability: computeHitProbability(k),
  };
}

export function buildTicketQuoteFromRound(round) {
  if (!round) return buildTicketQuote(0);
  const k = Math.max(0, Math.floor(Number(round.k) || (round.selectedNumbers || []).length));
  const quote = buildTicketQuote(k);

  if (round.reservedFromPool != null && Number(round.reservedFromPool) >= 0) {
    return {
      ...quote,
      reserve: Math.max(0, Math.round(Number(round.reservedFromPool))),
    };
  }

  if (round.maxPotentialPayout != null && Number(round.maxPotentialPayout) > 0) {
    return {
      ...quote,
      maxWin: Math.round(Number(round.maxPotentialPayout)),
      reserve: Math.max(0, Math.round(Number(round.maxPotentialPayout)) - quote.stake),
    };
  }

  return quote;
}

export const formatDrawCoin = (value) => {
  const num = Math.floor(Number(value) || 0);
  return num.toLocaleString("en-US");
};

export function formatDrawPlayedAt(playedAt) {
  if (!playedAt) return null;
  const d = new Date(playedAt);
  if (Number.isNaN(d.getTime())) return null;
  const time = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  const y = new Date(today.getTime() - 86400000);
  const yStr = `${y.getFullYear()}-${String(y.getMonth() + 1).padStart(2, "0")}-${String(y.getDate()).padStart(2, "0")}`;
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (date === todayStr) return `Today ${time}`;
  if (date === yStr) return `Yesterday ${time}`;
  return `${d.getMonth() + 1}/${d.getDate()} ${time}`;
}

export function computePotentialFromRecent(recent) {
  if (!recent) return 0;
  if (recent.potentialWin != null) return Math.round(Number(recent.potentialWin) || 0);
  const k = Array.isArray(recent.lucky) ? recent.lucky.length : 0;
  const stake = Number(recent.stake || 0);
  if (k <= 0 || stake <= 0) return 0;
  const pricePerNumber = Math.max(PRICE_PER_NUMBER, Math.round(stake / k));
  const odds = computeOdds(k);
  const maxMatches = Math.min(k, DRAW_COUNT);
  return Math.round(stake + pricePerNumber * maxMatches * odds);
}

export function getDrawResultDisplay(recent) {
  if (!recent) return { text: "—", tone: "neutral" };
  const status = String(recent.status || "").toLowerCase();
  const stake = Number(recent.stake || 0);
  if (status === "won") {
    return { text: `+${formatDrawCoin(recent.actualWin)}`, tone: "win" };
  }
  if (status === "refunded") {
    return { text: `+${formatDrawCoin(stake)}`, tone: "refund" };
  }
  return { text: `-${formatDrawCoin(stake)}`, tone: "loss" };
}

export function formatDrawDateTime(date, time) {
  if (!date && !time) return null;
  const today = new Date().toISOString().split("T")[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];
  if (date === today) return `Today, ${time || ""}`.replace(/,\s*$/, "");
  if (date === yesterday) return `Yesterday, ${time || ""}`.replace(/,\s*$/, "");
  if (!date) return time || null;
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return [date, time].filter(Boolean).join(" ");
  return `${d.getMonth() + 1}/${d.getDate()}${time ? `, ${time}` : ""}`;
}

export function getInvestorSharePercent(recent) {
  if (!recent) return "0.0";
  if (recent.sharePercent != null) return Number(recent.sharePercent).toFixed(1);
  const inv = Number(recent.investment || 0);
  const poolSize = Number(recent.poolSize || 0);
  if (!Number.isFinite(inv) || !Number.isFinite(poolSize) || poolSize <= 0) return "0.0";
  const pct = (inv / poolSize) * 100;
  return Number.isFinite(pct) ? pct.toFixed(1) : "0.0";
}

export function computeInvestorPoolAggregate(recent) {
  if (!recent || recent.sharePercent == null) return null;
  const share = Number(recent.sharePercent) / 100;
  if (share <= 0) return null;
  const myPnl = Number(recent.profit || 0);
  if (myPnl >= 0) {
    return { kind: "profit", amount: myPnl / share };
  }
  return { kind: "loss", amount: Math.abs(myPnl) / share };
}

export function formatInvestorProfitDisplay(profit) {
  const value = Number(profit || 0);
  if (value === 0) return "—";
  return `${value >= 0 ? "+" : ""}${formatDrawCoin(value)}`;
}

export const BOND_DRAW_INVESTOR_FIELD_TIPS = {
  investment: "Your contribution allocated from your active balance to this round.",
  poolSize: "Total amount reserved from all investors to cover potential payout for this round.",
  share: "Your investment divided by pool size (%). Profit on losses and payout on wins are divided proportionally to this share.",
  potential: "If the player loses, this is added to your active balance (your share of the player's stake). If the player wins, see Payout.",
  payout: "If the player wins, this amount is deducted from your active balance as your share of the payout.",
  refunded: "Amount returned to your active balance when a round is cancelled.",
  yourPnl: "Your individual result for this round. If the player loses, you gain your share of the stake. If the player wins, you pay your share of the payout.",
  usedRefunded: "Of your reserved amount, 'Used' is the part actually needed for this round; 'Refunded' is returned to your active balance.",
  poolUsedRefunded: "Pool totals for the round. 'Used' is pool coverage actually consumed; 'Refunded' is returned to investors.",
  poolProfit: "Estimated total profit for the pool this round, based on your proportional share.",
  poolLoss: "Estimated total loss for the pool this round (payout paid), based on your proportional share.",
};

export function drawNumbersClient() {
  const pool = Array.from({ length: TOTAL_NUMBERS }, (_, i) => i + 1);
  const drawn = [];
  for (let i = 0; i < DRAW_COUNT; i += 1) {
    const idx = Math.floor(Math.random() * pool.length);
    drawn.push(pool[idx]);
    pool.splice(idx, 1);
  }
  return drawn;
}

export function countMatches(selected = [], drawn = []) {
  const selSet = new Set(selected);
  return drawn.filter((n) => selSet.has(n)).length;
}

export function getMatchedNumbers(selected = [], drawn = []) {
  const selSet = new Set(selected);
  return drawn.filter((n) => selSet.has(n));
}
